import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import Slider from "react-slick";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import "web/react/components/product-gallery/slick-theme.less";
import "web/react/components/product-gallery/slick.less";
import SVGIcon from "web/react/components/svg-icon/svg-icon";

import {
    Image,
    ProductGalleryImage,
} from "web/react/pages/product/product-image-gallery/product-gallery-image";
import { getSafariMajorVersion } from "web/react/utils/user-agent";
import * as styles from "./product-image-gallery-fullscreen.css";
import { ThumbnailStrip } from "./thumbnail-strip/thumbnail-strip";

interface ProductImageGalleryFullscreenProps {
    images: Image[];
    initialSlideIndex?: number;
    onClose: () => void;
}

interface ArrowProps {
    className?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

function PrevArrow({ className, onClick }: ArrowProps): React.ReactElement {
    return (
        <button
            className={clsx(styles.prevArrow, className)}
            onClick={onClick}
            aria-label="Previous image"
        >
            <SVGIcon name="chevron-thin" rotate={90} />
        </button>
    );
}

function NextArrow({ className, onClick }: ArrowProps): React.ReactElement {
    return (
        <button
            className={clsx(styles.nextArrow, className)}
            onClick={onClick}
            aria-label="Next image"
        >
            <SVGIcon name="chevron-thin" rotate={-90} />
        </button>
    );
}

export function ProductImageGalleryFullscreen({
    images,
    initialSlideIndex = 0,
    onClose,
}: ProductImageGalleryFullscreenProps): React.ReactElement {
    const isIOS18 = getSafariMajorVersion() === 18;
    const dialogRef = useRef<HTMLDialogElement | null>(null);
    const sliderRef = useRef<Slider | null>(null);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(initialSlideIndex);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (dialog) {
            dialog.showModal();
            dialog.focus();

            const handleCloseOnBackdrop = (e: MouseEvent): void => {
                if (e.target === dialog) {
                    onClose();
                }
            };

            dialog.addEventListener("click", handleCloseOnBackdrop);
            return () => dialog.removeEventListener("click", handleCloseOnBackdrop);
        }
    }, [onClose]);

    return ReactDOM.createPortal(
        <dialog
            ref={dialogRef}
            data-testid="fullscreen-gallery"
            onCancel={onClose}
            className={styles.overlay}
            tabIndex={-1}
        >
            <span className={styles.closeButton}>
                <CloseButton onClick={onClose} />
            </span>
            <Slider
                ref={sliderRef}
                speed={400}
                slidesToShow={1}
                centerMode={false}
                infinite={false}
                arrows={true}
                swipe
                touchMove
                draggable={false}
                dots={false}
                beforeChange={(_, newIndex) => setCurrentSlideIndex(newIndex)}
                prevArrow={<PrevArrow />}
                nextArrow={<NextArrow />}
                className={styles.slider}
                initialSlide={initialSlideIndex}
            >
                {images.map((image, index) => (
                    <ProductGalleryImage
                        key={`${image.id}-${index}`}
                        src={image.url}
                        alt={image.altText ?? undefined}
                        lazy={!isIOS18}
                        isFullscreen
                        blurImageOnLoad={!isIOS18}
                        tabIndex={-1}
                    />
                ))}
            </Slider>
            {images.length > 1 && (
                <ThumbnailStrip
                    images={images}
                    currentSlideIndex={currentSlideIndex}
                    onThumbnailImageClick={(index) => sliderRef.current?.slickGoTo(index)}
                />
            )}
        </dialog>,
        document.body
    );
}
